import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  Chip,
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

const ChangeUplineDialog = ({ open, onClose, onConfirm, user, users, loading }) => {
  const [selectedParent, setSelectedParent] = useState(null);
  const [search, setSearch] = useState('');

  const resetState = () => {
    setSelectedParent(null);
    setSearch('');
  };

  React.useEffect(() => {
    if (open) resetState();
  }, [open]);

  const filteredUsers = useMemo(() => {
    if (!users || !user) return [];
    return users.filter((u) => {
      if (u.iboNumber === user.iboNumber) return false;
      if (!search.trim()) return true;
      const s = search.toLowerCase();
      return (
        (u.iboNumber && u.iboNumber.toLowerCase().includes(s)) ||
        (u.iboName && u.iboName.toLowerCase().includes(s)) ||
        (u.email && u.email.toLowerCase().includes(s))
      );
    });
  }, [users, user, search]);

  const handleConfirm = () => {
    if (!selectedParent) return;
    onConfirm({
      userId: user.id,
      newUplinePlatinumIbo: selectedParent.iboNumber,
      oldParentIbo: user.uplinePlatinumIbo || null,
    });
  };

  if (!user) return null;

  const currentUpline = user.uplinePlatinumIbo
    ? users?.find((u) => u.iboNumber === user.uplinePlatinumIbo)
    : null;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SwapHorizIcon color="primary" />
        Change Upline
      </DialogTitle>

      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          This will mark the user as <strong>override</strong> — Amway KPI will
          not change this user's upline automatically until the override is cleared.
        </Alert>

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{user.iboName || 'Unknown'}</strong> (IBO: {user.iboNumber})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Current upline:{' '}
            {currentUpline
              ? `${currentUpline.iboName || 'Unknown'} (${currentUpline.iboNumber})`
              : user.uplinePlatinumIbo || 'None (root)'}
          </Typography>
        </Box>

        {user.uplineOverride && (
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            This user already has an active override. Changing the upline will update it.
          </Alert>
        )}

        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Select new upline:
        </Typography>

        <TextField
          size="small"
          fullWidth
          placeholder="Search by IBO, name, or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: 1 }}
        />

        <List
          dense
          sx={{
            maxHeight: 300,
            overflow: 'auto',
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
          }}
        >
          {filteredUsers.length === 0 ? (
            <Box sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                {search.trim() ? 'No users matching search' : 'No users available'}
              </Typography>
            </Box>
          ) : (
            filteredUsers.map((u) => {
              const isCurrentUpline = u.iboNumber === user.uplinePlatinumIbo;
              const isSelected = selectedParent?.iboNumber === u.iboNumber;
              return (
                <ListItemButton
                  key={u.iboNumber}
                  selected={isSelected}
                  onClick={() => setSelectedParent(u)}
                  sx={{
                    borderRadius: 0.5,
                    mx: 0.5,
                    my: 0.25,
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                          {u.iboName || 'Unknown'}
                        </Typography>
                        {isCurrentUpline && (
                          <Chip label="current upline" size="small" color="info" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                        )}
                        {u.teamName && (
                          <Chip label={u.teamName} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                        )}
                      </Box>
                    }
                    secondary={`IBO: ${u.iboNumber}${u.email ? ` · ${u.email}` : ''}`}
                  />
                  {isSelected && <CheckCircleIcon color="primary" sx={{ fontSize: 20 }} />}
                </ListItemButton>
              );
            })
          )}
        </List>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={loading || !selectedParent}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? 'Updating...' : 'Confirm Change'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangeUplineDialog;
